import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 120000, // 2 minutes timeout for all requests
});

// Profile API
export const profileApi = {
  create: (data) => api.post('/profiles', data),
  get: (id) => api.get(`/profiles/${id}`),
  update: (id, data) => api.put(`/profiles/${id}`, data),
  merge: (id, data) => api.patch(`/profiles/${id}/merge`, data),
  delete: (id) => api.delete(`/profiles/${id}`),
};

// Chat Session API
export const sessionApi = {
  create: (data) => api.post('/sessions', data),
  get: (id) => api.get(`/sessions/${id}`),
  delete: (id) => api.delete(`/sessions/${id}`),
};

// Chat API
export const chatApi = {
  send: (sessionId, message, language = 'fr') => api.post('/chat', { session_id: sessionId, message, language }),
};

// Text-to-Speech API
export const ttsApi = {
  speak: (text, language = 'fr', voice = null, speed = 1.0) => 
    api.post('/tts', { text, language, voice, speed }),
};

// Speech-to-Text API (Whisper)
export const sttApi = {
  transcribe: async (audioBlob, language = 'fr') => {
    const formData = new FormData();
    // Ensure proper file extension for Whisper
    const filename = `audio.${audioBlob.type?.includes('webm') ? 'webm' : 'mp3'}`;
    formData.append('audio', audioBlob, filename);
    formData.append('language', language);
    return api.post('/stt', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 1 minute for transcription
    });
  },
};

// CV API
export const cvApi = {
  upload: async (file, language = 'fr') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    return api.post('/cv/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 180000, // 3 minutes for CV upload (AI processing takes time)
    });
  },
  parseText: (profileId, cvText) => api.post('/cv/parse', { profile_id: profileId, cv_text: cvText }),
};

// Job Queue API for async processing
export const jobApi = {
  /**
   * Get the status of an async job
   * @param {string} jobId - The job ID
   * @returns {Promise} Job status object
   */
  getStatus: (jobId) => api.get(`/jobs/${jobId}`),
  
  /**
   * Cancel a pending job
   * @param {string} jobId - The job ID
   * @returns {Promise} Cancellation result
   */
  cancel: (jobId) => api.post(`/jobs/${jobId}/cancel`),
  
  /**
   * Get queue statistics
   * @returns {Promise} Queue stats
   */
  getQueueStats: () => api.get('/jobs/queue/stats'),
  
  /**
   * Poll for job completion with progress callback
   * @param {string} jobId - The job ID
   * @param {Function} onProgress - Callback for progress updates (0-100)
   * @param {number} interval - Polling interval in ms (default 1000)
   * @param {number} timeout - Max wait time in ms (default 180000 = 3 min)
   * @returns {Promise} Final job result
   */
  pollUntilComplete: async (jobId, onProgress = null, interval = 1000, timeout = 180000) => {
    const startTime = Date.now();
    
    while (Date.now() - startTime < timeout) {
      try {
        const response = await api.get(`/jobs/${jobId}`);
        const status = response.data;
        
        // Call progress callback if provided
        if (onProgress && status.progress !== undefined) {
          onProgress(status.progress, status.status);
        }
        
        // Check if job is complete
        if (status.status === 'completed') {
          return { success: true, ...status };
        }
        
        // Check if job failed
        if (status.status === 'failed' || status.status === 'cancelled') {
          return { 
            success: false, 
            error: status.error || 'Job failed',
            ...status 
          };
        }
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, interval));
        
      } catch (error) {
        console.error('Error polling job status:', error);
        // Continue polling on transient errors
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }
    
    // Timeout
    return { success: false, error: 'Job timed out', status: 'timeout' };
  }
};

// Document API with async support
export const documentApi = {
  /**
   * Analyze a document synchronously (existing endpoint)
   */
  analyze: async (file, documentType, profileId, sessionId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('profile_id', profileId);
    if (sessionId) {
      formData.append('session_id', sessionId);
    }
    return api.post('/document/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000, // 3 minutes
    });
  },
  
  /**
   * Analyze a document asynchronously (new endpoint)
   * Returns a job_id for polling
   */
  analyzeAsync: async (file, documentType, profileId, sessionId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);
    formData.append('profile_id', profileId);
    if (sessionId) {
      formData.append('session_id', sessionId);
    }
    return api.post('/document/analyze-async', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000, // 30 seconds just for submission
    });
  },
  
  /**
   * Analyze a document with progress tracking
   * Uses async endpoint with polling
   */
  analyzeWithProgress: async (file, documentType, profileId, sessionId = null, onProgress = null) => {
    try {
      // Submit for async processing
      const submitResponse = await documentApi.analyzeAsync(file, documentType, profileId, sessionId);
      const jobId = submitResponse.data.job_id;
      
      if (!jobId) {
        throw new Error('No job ID returned');
      }
      
      // Poll until complete
      const result = await jobApi.pollUntilComplete(jobId, onProgress);
      return result;
      
    } catch (error) {
      console.error('Document analysis error:', error);
      throw error;
    }
  }
};

// Custom CV Sections API
export const customSectionsApi = {
  // References
  addReference: (profileId, data) => api.post(`/profiles/${profileId}/references`, data),
  deleteReference: (profileId, referenceId) => api.delete(`/profiles/${profileId}/references/${referenceId}`),
  
  // Volunteering
  addVolunteering: (profileId, data) => api.post(`/profiles/${profileId}/volunteering`, data),
  deleteVolunteering: (profileId, volunteeringId) => api.delete(`/profiles/${profileId}/volunteering/${volunteeringId}`),
  
  // Publications
  addPublication: (profileId, data) => api.post(`/profiles/${profileId}/publications`, data),
  deletePublication: (profileId, publicationId) => api.delete(`/profiles/${profileId}/publications/${publicationId}`),
  
  // Interests
  updateInterests: (profileId, interests) => api.put(`/profiles/${profileId}/interests`, interests),
  
  // Custom Sections
  addCustomSection: (profileId, data) => api.post(`/profiles/${profileId}/custom-sections`, data),
  updateCustomSection: (profileId, sectionId, data) => api.put(`/profiles/${profileId}/custom-sections/${sectionId}`, data),
  deleteCustomSection: (profileId, sectionId) => api.delete(`/profiles/${profileId}/custom-sections/${sectionId}`),
};

export default api;
